import { RefrigerantTypeService } from './../../../../@core/utils/refrigerant-type.service';
import { PerimeterEnergyTrajectoryService } from './../../../../@core/utils/perimeter-energy-trajectory.service';
import { NbAccordionModule, NbDialogService, NbToastrService } from '@nebular/theme';
import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { SaveToastService } from '../../../../@core/utils';
import { KPI } from '../../../../@core/api';
import { Subscription, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslateModule, TranslateService, } from '@ngx-translate/core';
import { DeclareRefrigerantFluidDialogComponent } from '../declare-refrigerant-fluid-dialog/declare-refrigerant-fluid-dialog.component';
import { AsyncPipe, DatePipe, DecimalPipe } from '@angular/common';
import { BasicDialogComponent } from '../..';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';

export type DisplayedRefrigerantFluid = {
  perimeter: string,
  refrigerant: string,
  gwp_20_years: number,
  year: number,
  emission: number,
  updated_on: string,
  last_reload: string,
  trajectory_id: number,
  refrigerant_type_id: number,
  trajectory_record_id: number,
  reloaded_quantity: number
};

@Component({
    selector: 'app-site-refrigerant-fluids',
    templateUrl: './site-refrigerant-fluids.component.html',
    styleUrls: ['./site-refrigerant-fluids.component.scss'],
    standalone: true,
    imports: [
      TranslateModule,
      NbAccordionModule,
      AsyncPipe,
      DecimalPipe,
      DatePipe
    ]
})
export class SiteRefrigerantFluidsComponent implements OnInit, OnDestroy {

  private translate = inject(TranslateService);

  @Input() perimeter: KPI.Perimeter;
  @Input() readOnly: boolean = false;

  public refrigerantFluids$: Observable<DisplayedRefrigerantFluid[]>;
  public refrigerantTypes$: Observable<KPI.RefrigerantType[]> = this.refrigerantTypeService.refrigerantTypes$;
  private subscriptions: Subscription[] = [];

  constructor(
    private dialogService: NbDialogService,
    private perimeterEnergyTrajectoryService: PerimeterEnergyTrajectoryService,
    private refrigerantTypeService: RefrigerantTypeService,
    private toastrService: NbToastrService,
    private saveToast: SaveToastService
  ) { }

  ngOnInit(): void {
    if (this.perimeter) {
      this.refrigerantFluids$ = combineLatest([
        this.perimeterEnergyTrajectoryService.getRefrigerantPerimeterEnergyTrajectories(
          this.perimeter.id
        ),
        this.refrigerantTypes$,
      ]).pipe(
        map(([trajectories, refrigerantTypes]) => {

          let trajectoryRecords: DisplayedRefrigerantFluid[] = trajectories.reduce((acc, trajectory) => {
            acc.push(...trajectory.trajectory_records.map((record) => ({
              ...record,
              perimeter: this.perimeter.sub_perimeters.find((elt) => elt.id === trajectory.perimeter)?.name,
              refrigerant: refrigerantTypes.find((elt) => elt.id === trajectory.refrigerant_type)?.name,
              gwp_20_years: refrigerantTypes.find((elt) => elt.id === trajectory.refrigerant_type)?.gwp_20_years,
              trajectory_id: trajectory.id,
              refrigerant_type_id: trajectory.refrigerant_type,
              trajectory_record_id: record.id,
            })));
            return acc;
          }, []).sort((a, b) =>
            this.dateTime(b.updated_on) - this.dateTime(a.updated_on),
          );

          return trajectoryRecords.map((record) => ({
            ...record,
            last_reload: trajectoryRecords.find((r) =>
              this.dateTime(r.updated_on) < this.dateTime(record.updated_on)
              && r.perimeter === record.perimeter
              && r.refrigerant === record.refrigerant
            )?.updated_on,
            reloaded_quantity: parseFloat((record.emission / record.gwp_20_years).toFixed(2))
          }))
        }),
      );
    }
  }

  private dateTime(date: string): number {
    return new Date(date).getTime();
  }

  public openDeclareRefrigerantFluidDialogDialog(): void {
    this.dialogService.open(
      DeclareRefrigerantFluidDialogComponent, {
      context: {
        site: this.perimeter,
      },
    }).onClose.subscribe((result) => {
      if (result) {
        this.saveToast.showSaved();
      }
    });
  }

  public editRefrigerantFluid(refrigerant: DisplayedRefrigerantFluid): void {
    this.dialogService.open(
      DeclareRefrigerantFluidDialogComponent, {
      context: {
        site: this.perimeter,
        editData: refrigerant,
      },
    }).onClose.subscribe((result) => {
      if (result) {
        this.saveToast.showSaved();
      }
    });
  }

  showNotPermissionDialog(): void {
    this.dialogService.open(
      BasicDialogComponent, {
        context: {
          title: this.translate.instant('Access denied'),
          content: this.translate.instant('You do not have modification rights for this part of the software'),
          canCancel: false
        },
        autoFocus: false
      }
    )
  }

  public confirmDeleteRefrigerantFluid(trajectoryId: number, refrigerantName: string, perimeterName: string): void {
    const dialogRef = this.dialogService.open(ConfirmationDialogComponent, {
      context: {
        label: this.translate.instant('Are you sure you want to delete the refrigerant fluid declaration for {{perimeterName}} - {{refrigerantName}}?', { perimeterName, refrigerantName })
      },
      closeOnBackdropClick: false,
      closeOnEsc: false,
    });

    dialogRef.onClose.subscribe((result) => {
      if (result) {
        this.deleteRefrigerantFluid(trajectoryId);
      }
    });
  }

  private deleteRefrigerantFluid(trajectoryId: number): void {
    this.saveToast.showSaving();
    this.subscriptions.push(
      this.perimeterEnergyTrajectoryService.deletePerimeterEnergyTrajectory(trajectoryId).subscribe({
        next: () => {
          this.refrigerantTypeService.refrigerantFluidHasChanged();
          this.saveToast.showSaved();
        },
        error: (error) => {
          console.error('Error deleting refrigerant fluid:', error);
          this.saveToast.close();
          this.toastrService.danger(this.translate.instant('Error deleting refrigerant fluid declaration'), this.translate.instant('Error'));
        }
      })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
